/******************************************************************************
 *
 * FILE:
 * src/modules/wms/workspace/executor/pages/ExecutorWorkspacePage.jsx
 *
 * MODULE:
 * Work Management System (WMS)
 *
 * FEATURE:
 * Executor Workspace
 *
 * DESCRIPTION:
 * Main landing page for executor users.
 *
 * RESPONSIBILITIES
 * ----------------
 * ✓ Load workspace
 * ✓ Display KPI summary
 * ✓ Display Resume Work
 * ✓ Display Highest Priority Work
 * ✓ Display Advanced Filters
 * ✓ Display My Work
 * ✓ Refresh Workspace
 *
 ******************************************************************************/

import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";

import ExecutorWorkspaceKpiBar
from "../components/ExecutorWorkspaceKpiBar";

import ResumeWorkCard
from "../components/ResumeWorkCard";

import PriorityWorkCard
from "../components/PriorityWorkCard";

import ExecutorWorkspaceFilterBar
from "../components/ExecutorWorkspaceFilterBar";

import ExecutorWorkspaceTable
from "../components/ExecutorWorkspaceTable";

import wmsExecutorWorkspaceService
from "../services/wmsExecutorWorkspaceService";

import CommentsModal
from "../../components/CommentsModal";

export default function ExecutorWorkspacePage() {

    // ==========================================================
    // STATE
    // ==========================================================

    const [loading, setLoading] =
        useState(false);

    const [summary, setSummary] =
        useState({});

    const [resumeWork, setResumeWork] =
        useState(null);

    const [priorityWork, setPriorityWork] =
        useState(null);

    const [rows, setRows] =
        useState([]);

    const [filters, setFilters] =
        useState({});

    const [activeKpi, setActiveKpi] =
        useState("");

    const navigate = useNavigate();

    const [showComments, setShowComments] =
    useState(false);

    const [selectedWork, setSelectedWork] =
        useState(null);

    const [commentWorkspace, setCommentWorkspace] =
        useState(null);
    // ==========================================================
    // LOAD
    // ==========================================================

    const loadWorkspace =
        useCallback(async () => {

            try {

                setLoading(true);

                const result =
                    await wmsExecutorWorkspaceService
                        .getExecutorWorkspace(filters);

                setSummary(
                    result.summary || {}
                );

                setResumeWork(
                    result.resumeWork || null
                );

                setPriorityWork(
                    result.priorityWork || null
                );

                setRows(
                    result.works || []
                );

            }

            catch (err) {

                console.error(err);

            }

            finally {

                setLoading(false);

            }

        }, [filters]);

    useEffect(() => {

        loadWorkspace();

    }, [loadWorkspace]);

    // ==========================================================
    // REFRESH
    // ==========================================================

    function handleRefresh() {

        loadWorkspace();

    }

    // ==========================================================
    // KPI CLICK
    // ==========================================================

    function handleKpiSelect(status) {

        setActiveKpi(status);

        setFilters((prev) => ({

            ...prev,

            status,

        }));

    }

    // ==========================================================
    // RESET
    // ==========================================================

    function handleReset() {

        setActiveKpi("");

        setFilters({});

    }

    // ==========================================================
    // EXECUTE WORK
    // ==========================================================

    function handleExecuteWork(workId) {

        navigate(`/wms/works/${workId}/execute`);

    }

    // ==========================================================
    // COMMENTS
    // ==========================================================

    async function handleComments(work) {

        try {

            setSelectedWork(work);

            const workspace =
                await wmsExecutorWorkspaceService
                    .getExecutionWorkspace(work.id);

            setCommentWorkspace(workspace);

            setShowComments(true);

        }

        catch (error) {

            console.error(
                "Failed to load comments",
                error
            );

        }

    }
    // ==========================================================
    // COMMENTS
    // ==========================================================

    async function handleComments(work) {

        try {

            setSelectedWork(work);

            const workspace =
                await wmsExecutorWorkspaceService
                    .getExecutionWorkspace(work.id);

            setCommentWorkspace(workspace);

            setShowComments(true);

        }

        catch (error) {

            console.error(
                "Failed to load comments",
                error
            );

        }

    }
    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <div className="container-fluid">

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <div
                className="card border-0 mb-4"
                style={{
                    background:
                        "linear-gradient(90deg,#0d6efd,#4f8cff)",
                    borderRadius: 12,
                    color: "#fff",
                }}
            >

                <div
                    className="
                        card-body
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <div>

                        <h2 className="mb-1">

                            My Workspace

                        </h2>

                        <div>

                            Execute and manage your assigned work

                        </div>

                    </div>

                    <button
                        className="btn btn-light"
                        onClick={handleRefresh}
                    >

                        <i className="fas fa-sync-alt me-2" />

                        Refresh

                    </button>

                </div>

            </div>

            {/* ====================================================== */}
            {/* KPI */}
            {/* ====================================================== */}

            <ExecutorWorkspaceKpiBar

                summary={summary}

                activeFilter={activeKpi}

                onSelect={handleKpiSelect}

            />

            {/* ====================================================== */}
            {/* FOCUS */}
            {/* ====================================================== */}

            <div className="row mb-4">

                <div className="col-lg-6 mb-3">

                    <ResumeWorkCard
                        work={resumeWork}
                        onExecute={handleExecuteWork}
                    />

                </div>

                <div className="col-lg-6 mb-3">

                    <PriorityWorkCard
                        work={priorityWork}
                        onExecute={handleExecuteWork}
                    />

                </div>

            </div>

            {/* ====================================================== */}
            {/* FILTER */}
            {/* ====================================================== */}

            <ExecutorWorkspaceFilterBar

                filters={filters}

                onChange={setFilters}

                onReset={handleReset}

            />

            {/* ====================================================== */}
            {/* TABLE */}
            {/* ====================================================== */}

            <ExecutorWorkspaceTable

                rows={rows}

                loading={loading}

                onExecute={handleExecuteWork}
                onComments={handleComments}

            />

        <CommentsModal

            show={showComments}

            onClose={() => {

                setShowComments(false);

                setSelectedWork(null);

                setCommentWorkspace(null);

            }}

            comments={commentWorkspace?.comments || []}

            onAddComment={async (text) => {

                await wmsExecutorWorkspaceService.addComment(

                    selectedWork.id,

                    text

                );

                const workspace =
                    await wmsExecutorWorkspaceService
                        .getExecutionWorkspace(
                            selectedWork.id
                        );

                setCommentWorkspace(workspace);

            }}

            title={
                selectedWork
                    ? `Comments - ${selectedWork.work_no}`
                    : "Comments"
            }

        />
        
        </div>

    );

}